import DateFnsUtils from '@date-io/date-fns';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import useInterval from '@use-it/interval';
import {APIResponseStatus, Locale, NormalizedRoutes} from 'common/types';
import dateFnsLocaleRu from 'date-fns/locale/ru';
import dateFnsLocaleUk from 'date-fns/locale/uk';
import {usePageviewTracker} from 'modules/ga';
import {compose} from 'ramda';
import React, {FunctionComponent, lazy, Suspense, useEffect} from 'react';
import {Helmet} from 'react-helmet';
import {connect, useDispatch} from 'react-redux';
import {Redirect, Route, Switch} from 'react-router-dom';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import GuestRoute from 'screens/common/GuestRoute';
import Loading from 'screens/common/Loading';
import MainLayout from 'screens/MainLayout';
import {useMarketFirstVisit} from 'screens/MarketActivityScreen/hooks';
import {SubscriptionAPI} from 'services/SubscriptionAPI';
import {AppState} from 'store';
import * as I18nSelectors from 'store/i18n/selectors';
import {setProSubscription} from 'store/subscription/reducer';
import {useFetchUserSettings} from '../../services/UsersSettingsAPI';

const LOCALE_MAP = {
  [Locale.RU]: dateFnsLocaleRu,
  [Locale.UK]: dateFnsLocaleUk,
};

const mapStateToProps = (state: AppState) => ({
  locale: I18nSelectors.locale(state),
});

const LoginScreen = lazy(() => import('screens/LoginScreen'));
const ReferralParkingScreen = lazy(() => import('screens/ReferralParkingScreen'));
const RegistrationScreen = lazy(() => import('screens/RegistrationScreen'));
const RegistrationInfoScreen = lazy(
  () => import('screens/RegistrationInfoScreen')
);
const ResetPasswordScreen = lazy(() => import('screens/ResetPasswordScreen'));
const RestoreUserScreen = lazy(() => import('screens/RestoreUserScreen'));
const HosingCostCalculationRequestScreen = lazy(
  () => import('screens/HosingCostCalculationRequestScreen')
);

const GameQuizScreen = lazy(() =>
  import('screens/GameQuiz')
);
const Conference2025 = lazy(()=>import('screens/Conference2025'));
interface RootProps
  extends RouteComponentProps,
    ReturnType<typeof mapStateToProps> {}

const Root: FunctionComponent<RootProps> = ({location, locale}: RootProps) => {
  usePageviewTracker(location);

  // Check user subscription status every 5 minutes
  const dispatch = useDispatch();
  const subApi = new SubscriptionAPI();
  useInterval(() => {
    subApi.fetchSubscriptionInfo()
      .then(response => {
        if (response.status === APIResponseStatus.Ok) {
          dispatch(setProSubscription(response.data));
        }
      })
      .catch(error => {
        console.debug(error);
      });
  }, 1000 * 60 * 5);

  // Track location changes for propper Market activity list reload
  const [firstVisit, setFirstVisit] = useMarketFirstVisit();
  useEffect(() => {
    if (location.pathname !== NormalizedRoutes.MarketActivity) {
      setFirstVisit(true);
    }
  }, [location]);

  // Fetch user settings once
  useFetchUserSettings();

  return (
    <MuiPickersUtilsProvider
      utils={DateFnsUtils}
      locale={LOCALE_MAP[locale as Locale]}
    >
      <>
        <Helmet>
          <html lang={locale as string} />
        </Helmet>
        <Suspense fallback={<Loading />}>
          <Switch>
            <GuestRoute path="/login" exact component={LoginScreen} />
            <GuestRoute path="/r/:phone" exact component={ReferralParkingScreen} />
            <GuestRoute
              path="/registration"
              exact
              component={RegistrationScreen}
            />
            <GuestRoute
              path="/restore-user"
              exact
              component={RestoreUserScreen}
            />
            <GuestRoute
              path="/registration/info"
              exact
              component={RegistrationInfoScreen}
            />
            <GuestRoute
              path="/reset-password"
              exact
              component={ResetPasswordScreen}
            />
            <Route
              path="/housing-cost-calculation-request"
              exact
              component={HosingCostCalculationRequestScreen}
            />
            <Route
              path={[
                '/offers',
                '/profile',
                '/agents-suggestions',
                '/payment',
                '/deals',
                '/referrals',
                '/dashboard',
                NormalizedRoutes.Rieltors,
                '/olx-migration',
                '/rieltor-manifest',
                '/internet-offers',
                '/agency-feed',
                // '/conference2025',
                NormalizedRoutes.MarketActivity,
                NormalizedRoutes.Objects,
                  NormalizedRoutes.IbanPayments
              ]}
              component={MainLayout}
            />
            {/*<Route*/}
            {/*    path={'/conference2025'}*/}
            {/*    component={Conference2025}*/}
            {/*/>*/}
            <Route
              path={NormalizedRoutes.RieltorsGame}
              component={GameQuizScreen}
            />
            <GuestRoute
              path={NormalizedRoutes.RieltorsGame}
              component={GameQuizScreen}
            />
            <Redirect to="/login" />
          </Switch>
        </Suspense>
      </>
    </MuiPickersUtilsProvider>
  );
};

const EnhancedRoot = compose(
  withRouter,
  // @ts-ignore
  connect(mapStateToProps)
)(Root);

export default EnhancedRoot;
